<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { singular } from 'pluralize'
import axios from 'src/api/mutator/axios'
import SharedEntitySelectDrawer from 'src/components/shared/SharedEntitySelectDrawer.vue'

const props = defineProps({
  context: Object,
})

const displayLabel = ref('')
const drawerVisible = ref(false)
const loadingEntity = ref(false)

const entity = computed(() => props.context?.entity ?? '')
const entityValue = computed(() => props.context?.entityValue ?? 'id')
const sourceField = computed(() => props.context?.sourceField as string | undefined)
const placeholder = computed(() => props.context?.placeholder ?? `Select ${singular(entity.value)}...`)

/**
 * Resolve initial value using the codegen-emitted sourceField.
 * If sourceField is "user", looks up formData["user"] → { id: "...", first_name: "..." }
 */
function resolveInitialValue(): { id: string; entityData: Record<string, unknown> } | null {
  if (props.context?.value) return null
  if (!sourceField.value) return null

  const formData = props.context?.node?.parent?.value as Record<string, unknown> | undefined
  if (!formData) return null

  const value = formData[sourceField.value]
  if (value && typeof value === 'object' && 'id' in (value as Record<string, unknown>)) {
    const obj = value as Record<string, unknown>
    return { id: String(obj.id), entityData: obj }
  }

  return null
}

function buildLabelFromObject(data: Record<string, unknown>): string {
  return Object.entries(data)
    .filter(([key, value]) =>
      key !== 'id' && value !== null && typeof value !== 'object' && !Array.isArray(value),
    )
    .map(([_, value]) => String(value))
    .filter(Boolean)
    .slice(0, 3)
    .join(' - ')
}

// Resolve from sourceField on mount
const resolved = resolveInitialValue()
const modelValue = ref(resolved?.id ?? props.context?.value)

if (resolved) {
  displayLabel.value = buildLabelFromObject(resolved.entityData)
  props.context?.node?.input(resolved.id)
}

// Sync FormKit value → local model
watch(() => props.context?.value, (newValue) => {
  if (newValue !== modelValue.value) {
    modelValue.value = newValue
    displayLabel.value = ''
  }
})

function handleDrawerSelect(value: string) {
  modelValue.value = value
  props.context?.node?.input(value)
}

function handleLabelUpdate(label: string) {
  displayLabel.value = label
}

function handleClear() {
  modelValue.value = null
  displayLabel.value = ''
  props.context?.node?.input(null)
}

async function fetchEntityLabel(id: string) {
  if (!entity.value || !id) return

  loadingEntity.value = true
  try {
    const { data } = await axios.get(`/api/${singular(entity.value)}/${id}`)
    if (data) {
      displayLabel.value = buildLabelFromObject(data)
    }
  } catch {
    displayLabel.value = id
  } finally {
    loadingEntity.value = false
  }
}

// Fetch display label when modelValue is a string ID without a label
watch(
  modelValue,
  (newValue) => {
    if (!newValue || typeof newValue !== 'string') {
      displayLabel.value = ''
      return
    }

    // Value is an object (from fetch response) — build label directly
    if (typeof newValue === 'object') {
      displayLabel.value = buildLabelFromObject(newValue as unknown as Record<string, unknown>)
      return
    }

    if (!displayLabel.value) {
      fetchEntityLabel(newValue)
    }
  },
  { immediate: true },
)

// Handle timing: entity name may resolve after modelValue on mount
watch(entity, () => {
  if (modelValue.value && typeof modelValue.value === 'string' && !displayLabel.value) {
    fetchEntityLabel(modelValue.value)
  }
})
</script>

<template>
  <div v-if="context" class="flex flex-col gap-1">
    <div
      class="flex items-center gap-2 w-full px-3 py-2 rounded-md border cursor-pointer transition-colors"
      :class="[
        'bg-surface-0 dark:bg-surface-900',
        'border-surface-300 dark:border-surface-600',
        'hover:border-primary',
      ]"
      @click="drawerVisible = true"
    >
      <i class="pi pi-search text-surface-400" style="font-size: 0.875rem" />
      <span
        class="flex-1 truncate"
        :class="displayLabel ? 'text-surface-700 dark:text-surface-0' : 'text-surface-400'"
      >
        {{ loadingEntity ? 'Loading...' : (displayLabel || placeholder) }}
      </span>
      <i
        v-if="modelValue && !loadingEntity"
        class="pi pi-times text-surface-400 hover:text-surface-600 dark:hover:text-surface-200"
        style="font-size: 0.75rem"
        @click.stop="handleClear"
      />
    </div>

    <SharedEntitySelectDrawer
      v-model:visible="drawerVisible"
      :data="modelValue"
      :entity="entity"
      :entity-value="entityValue"
      :requests="context.requests"
      @update:data="handleDrawerSelect"
      @update:label="handleLabelUpdate"
    />
  </div>
</template>
